import { useState } from 'react';

const BAR_FILLS = {
  'bar--high': '#d2555a',
  'bar--medium': '#f7a021',
  'bar--low': '#6dc4a3',
  'bar--default': '#7f8fa4',
  'bar--parent-portfolio': '#153142',
  'bar--portfolio': '#115973',
  'bar--communication': '#6dc4a3',
  'bar--readiness': '#24afdf',
  'bar--training': '#153142',
  'bar--sustainment': '#f7a021',
};

function applyGanttColors(svg) {
  svg.querySelectorAll('.bar').forEach((el) => {
    let fill = '#7f8fa4';
    for (const cls of el.classList) {
      if (BAR_FILLS[cls]) { fill = BAR_FILLS[cls]; break; }
    }
    el.setAttribute('fill', fill);
  });

  svg.querySelectorAll('.chart-label').forEach((el) => {
    el.setAttribute('fill', '#ffffff');
  });

  svg.querySelectorAll('.mile').forEach((el) => {
    el.setAttribute('fill', el.classList.contains('portfolio') ? '#5771ca' : '#27b0e0');
  });

  // Axis lines and domain path: d3 uses stroke/fill="currentColor" which
  // resolves to black in a standalone SVG without CSS. Hardcode from SCSS:
  // .axis { color: $silver (#dfe2e5) }
  svg.querySelectorAll('.axis path, .axis line').forEach((el) => {
    el.setAttribute('stroke', '#dfe2e5'); // $silver
    el.setAttribute('fill', 'none');
  });

  // Axis tick text: .axis .tick text { color: $blue-grey (#7f8fa4) }
  // Current-period labels: .current-month/.current-quarter/etc { color: $dark-blue-grey (#153142) }
  const CURRENT_CLASSES = ['current-day', 'current-month', 'current-quarter', 'current-year'];
  svg.querySelectorAll('.axis text').forEach((el) => {
    const isCurrent = CURRENT_CLASSES.some((cls) => el.classList.contains(cls));
    el.setAttribute('fill', isCurrent ? '#153142' : '#7f8fa4');
  });
}

export const useDownloadPNG = (chartContainerId, scaleType) => {
  const [loading, setLoading] = useState(false);

  const downloadPNG = async () => {
    const container = document.getElementById(chartContainerId);
    if (!container) return;

    setLoading(true);
    try {
      const svgs = Array.from(container.querySelectorAll('svg'));
      if (!svgs.length) return;

      const getContentWidth = (svg) => {
        const g = svg.querySelector('g');
        if (!g) return parseInt(svg.getAttribute('width') || 0);
        const bb = g.getBBox();
        return Math.ceil(bb.x + bb.width);
      };

      const mainSvg =
        container.querySelector('#ganttChartExport svg') ||
        container.querySelector('#ganttChartPdf svg') ||
        svgs[svgs.length - 1];

      const fullWidth = mainSvg
        ? getContentWidth(mainSvg)
        : Math.max(...svgs.map(getContentWidth));

      const totalHeight = svgs.reduce(
        (sum, svg) => sum + parseInt(svg.getAttribute('height') || 0),
        0
      );

      const SCALE = 2;
      const canvas = document.createElement('canvas');
      canvas.width = fullWidth * SCALE;
      canvas.height = totalHeight * SCALE;
      const ctx = canvas.getContext('2d');
      ctx.scale(SCALE, SCALE);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, fullWidth, totalHeight);

      let offsetY = 0;
      for (const svg of svgs) {
        const h = parseInt(svg.getAttribute('height') || 0);

        svg.setAttribute('width', fullWidth);
        svg.setAttribute('overflow', 'visible');
        svg.removeAttribute('viewBox');
        svg.querySelectorAll('clipPath rect').forEach((r) => {
          r.setAttribute('width', fullWidth);
        });

        svg.querySelectorAll('*').forEach((el) => {
          const cs = window.getComputedStyle(el);
          [
            'fill',
            'stroke',
            'stroke-width',
            'font-size',
            'font-family',
            'opacity',
          ].forEach((prop) => {
            const val = cs.getPropertyValue(prop);
            if (val) el.setAttribute(prop, val);
          });
        });

        applyGanttColors(svg);

        const svgBlob = new Blob([new XMLSerializer().serializeToString(svg)], {
          type: 'image/svg+xml',
        });
        const url = URL.createObjectURL(svgBlob);

        await new Promise((res, rej) => {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, offsetY, fullWidth, h);
            URL.revokeObjectURL(url);
            res();
          };
          img.onerror = rej;
          img.src = url;
        });

        offsetY += h;
      }

      const link = document.createElement('a');
      link.download = `gantt-chart-${scaleType}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('PNG generation failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return { downloadPNG, loading };
};
